import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import {
  deleteWhitelistIPAddress,
  disableWhitelistIPAddress,
  enableWhitelistIPAddress,
  getWhitelistIPAddresses,
} from "../../redux/slices/whitelist";
import NotyfContext from "../../contexts/NotyfContext";
import Loader from "../../components/spinners/Loader";
import { Helmet } from "react-helmet-async";
import {
  Col,
  Table,
  Form,
  Row,
  Pagination,
  Container,
  Card,
  Button,
  DropdownButton,
  ButtonGroup,
  Dropdown,
} from "react-bootstrap";
import useWindowSize from "../../hooks/useWindowSize";
import {
  FaPencilAlt,
  FaPlus,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaToggleOff,
  FaToggleOn,
  FaTrashAlt,
} from "react-icons/fa";
import getWhitelistData from "./whitelistData";
import {
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  defaultColumn,
} from "react-table";
import NewWhitelistIPModal from "./NewWhitelistIPModal";
import EditWhitelistIPModal from "./EditWhitelistIPModal";
import useDeviceType from "../../hooks/useDeviceType";

const WhitelistIP = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { whitelist } = useSelector((state) => state.whitelist);
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const notyf = useContext(NotyfContext);
  const [activeUser, setActiveUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { backgroundColor, fontColor, faviconUrl } = useWhitelabel();
  const [showNewWhitelistIpModal, setShowNewWhitelistIpModal] = useState(false);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const { whitelistColumns } = useMemo(() => getWhitelistData(t), [t]);
  const [addressToEdit, setAddressToEdit] = useState({});
  const [showEditIPAddress, setShowEditIPAddress] = useState(false);

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);

  useEffect(() => {
    const fetchWhitelistedIPAddresses = async () => {
      await dispatch(getWhitelistIPAddresses())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (activeUser.id) {
      fetchWhitelistedIPAddresses();
    }
  }, [dispatch, activeUser, notyf]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: whitelistColumns,
      data: whitelist,
      initialState: { pageIndex: 0 },
      defaultColumn,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: "Actions",
          sortable: false,
          Header: "Actions",
          Cell: ({ row }) => (
            <DropdownButton
              as={ButtonGroup}
              title="Actions"
              variant="primary"
              data-boundary="viewport"
              style={{
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
                border: "none",
              }}
            >
              <Dropdown.Item
                eventKey="edit"
                onClick={() => {
                  setAddressToEdit(row.original);
                  setShowEditIPAddress(true);
                }}
              >
                <FaPencilAlt />
                <span className="ms-2">Edit</span>
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="delete"
                onClick={async () => {
                  await dispatch(deleteWhitelistIPAddress(row.original.id))
                    .then(() => {
                      notyf.open({
                        type: "success",
                        message: "Whitelist IP Address deleted successfully",
                        duration: 5000,
                        ripple: true,
                        dismissible: false,
                        position: {
                          x: "center",
                          y: "top",
                        },
                      });
                    })
                    .catch((error) => {
                      notyf.open({
                        type: "danger",
                        message: error,
                        duration: 5000,
                        ripple: true,
                        dismissible: false,
                        position: {
                          x: "center",
                          y: "top",
                        },
                      });
                    });
                }}
              >
                <FaTrashAlt />
                <span className="ms-2">Delete</span>
              </Dropdown.Item>
              {!!!row.original.active && (
                <Dropdown.Item
                  eventKey="enable"
                  onClick={async () => {
                    await dispatch(
                      enableWhitelistIPAddress({ id: row.original.id })
                    )
                      .then(() => {
                        notyf.open({
                          type: "success",
                          message: "Whitelist IP Address enabled successfully",
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: {
                            x: "center",
                            y: "top",
                          },
                        });
                      })
                      .catch((error) => {
                        notyf.open({
                          type: "danger",
                          message: error,
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: {
                            x: "center",
                            y: "top",
                          },
                        });
                      });
                  }}
                >
                  <FaToggleOn />
                  <span className="ms-2">Enable</span>
                </Dropdown.Item>
              )}
              {!!row.original.active && (
                <Dropdown.Item
                  eventKey="disable"
                  onClick={async () => {
                    await dispatch(
                      disableWhitelistIPAddress({ id: row.original.id })
                    )
                      .then(() => {
                        notyf.open({
                          type: "success",
                          message: "Whitelist IP Address disabled successfully",
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: {
                            x: "center",
                            y: "top",
                          },
                        });
                      })
                      .catch((error) => {
                        notyf.open({
                          type: "danger",
                          message: error,
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: {
                            x: "center",
                            y: "top",
                          },
                        });
                      });
                  }}
                >
                  <FaToggleOff />
                  <span className="ms-2">Disable</span>
                </Dropdown.Item>
              )}
            </DropdownButton>
          ),
        },
      ]);
    }
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Helmet title="Whitelist IP">
        <link ref="icon" href={faviconUrl} />
      </Helmet>
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">{t("whitelist.title")}</h1>
        <Card>
          <Card.Body>
            {isDesktop && (
              <Row className="g-0">
                <Col sm="12" xl="12" className="col-xxl-12">
                  <div className="d-flex mt-2 float-end">
                    <Button
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                      onClick={() => setShowNewWhitelistIpModal(true)}
                    >
                      <FaPlus />
                      {t("whitelist.new")}
                    </Button>
                  </div>
                </Col>
              </Row>
            )}
            <Table id="invoicesTable" hover responsive {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        <span {...column.getSortByToggleProps()}>
                          {column.render("Header")}
                          <span>
                            {column.sortable ? (
                              column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FaSortUp className="ms-2 " />
                                ) : (
                                  <FaSortDown className="ms-2 " />
                                )
                              ) : (
                                <FaSort className="ms-2" />
                              )
                            ) : null}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <EditWhitelistIPModal
              showEditWhitelistIPModal={showEditIPAddress}
              setShowEditWhitelistIPModal={setShowEditIPAddress}
              ipAddressToEdit={addressToEdit}
            />
            {isDesktop && (
              <Row>
                <Col md="8">
                  <span className="mx-2">
                    {t("table.page")}{" "}
                    <strong>
                      {pageIndex + 1} {t("table.of")} {pageOptions.length}
                    </strong>
                  </span>
                  <span className="ms-3 me-2">{t("table.show")}:</span>
                  <Form.Select
                    className="d-inline-block w-auto"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </Form.Select>

                  <span className="ms-3 me-2">{t("table.goToPage")}:</span>
                  <Form.Control
                    className="d-inline-block"
                    type="number"
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      gotoPage(page);
                    }}
                    style={{ width: "75px" }}
                  />
                </Col>
                <Col md="4">
                  <Pagination className="float-end">
                    <Pagination.First
                      onClick={() => {
                        gotoPage(0);
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Prev
                      onClick={() => {
                        previousPage();
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Next
                      onClick={() => {
                        nextPage();
                      }}
                      disabled={!canNextPage}
                    />
                    <Pagination.Last
                      onClick={() => {
                        gotoPage(pageCount - 1);
                      }}
                      disabled={!canNextPage}
                    />
                  </Pagination>
                </Col>
              </Row>
            )}
            {!isDesktop && (
              <React.Fragment>
                <div className="invoices-pagination mt-3">
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-center">
                      <Pagination className="float-end">
                        <Pagination.Prev
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        />
                        <Col className="d-flex align-items-end mx-2 px-3">
                          <span className="mx-2">
                            <strong>
                              {pageIndex + 1} {t("table.of")}{" "}
                              {pageOptions.length}
                            </strong>
                          </span>
                        </Col>
                        <Pagination.Next
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        />
                      </Pagination>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs="auto">
                      <span className="me-2">{t("table.show")}:</span>
                      <Form.Select
                        className="d-inline-block w-auto"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                        }}
                        style={{
                          padding: "0.25rem 2.1rem 0.25rem 0.7rem",
                          border: "none",
                          boxShadow: "none",
                          fontSize: "14px",
                        }}
                      >
                        {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col>
                      <div className="float-end">
                        <span className="me-2">{t("table.goToPage")}:</span>
                        <Form.Control
                          className="d-inline-block w-auto"
                          type="number"
                          defaultValue={pageIndex + 1}
                          min={1}
                          max={pageCount}
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            gotoPage(page);
                          }}
                          style={{
                            padding: "0.25rem 1.2rem 0.25rem 0.7rem",
                            fontSize: "0.825rem",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </React.Fragment>
            )}
          </Card.Body>
          <NewWhitelistIPModal
            showNewWhitelistIPModal={showNewWhitelistIpModal}
            setShowNewWhitelistIPModal={setShowNewWhitelistIpModal}
          />
        </Card>
      </Container>
    </React.Fragment>
  );
};

export default WhitelistIP;
